import { useTranslation } from 'react-i18next';
import { Reply, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { RichTextEditor } from '@/shared/components/custom/rich-text-editor';
import { formatTimeAgo } from '@/shared/utils/formatTimeAgo';
import { getInitials } from '@/core/lib/get-initials';
import type { TaskCommentDto } from '../../types';

interface CommentItemProps {
  comment: TaskCommentDto;
  currentUserId: string | null;
  isReply?: boolean;
  onReply: (commentId: string, authorName: string) => void;
  onDelete: (commentId: string) => void;
}

function safeContent(raw: string | null): string {
  if (!raw) return '';
  try {
    JSON.parse(raw);
    return raw;
  } catch {
    return raw;
  }
}

export function CommentItem({
  comment,
  currentUserId,
  isReply = false,
  onReply,
  onDelete,
}: CommentItemProps) {
  const { t } = useTranslation('tasks');

  const authorName = comment.authorName ?? '';
  const isOwn = !!currentUserId && currentUserId === comment.authorId;
  const content = safeContent(comment.content);

  return (
    <div
      className={`flex gap-2.5 py-2.5 ${isReply ? 'pl-8' : ''}`}
      data-testid="tasks-comment-item"
    >
      <Avatar className="mt-0.5 h-6 w-6 shrink-0">
        <AvatarFallback className="text-[9px]">{getInitials(authorName)}</AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-foreground">{authorName}</span>
          <span className="text-[10px] text-muted-foreground/70">
            {formatTimeAgo(comment.createdAt)}
          </span>
        </div>

        <div className="mt-1 text-xs text-foreground">
          <RichTextEditor value={content} onChange={() => undefined} readOnly />
        </div>

        <div className="mt-1 flex items-center gap-0.5">
          {!isReply && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  aria-label={t('activity.comment.replyAria')}
                  data-testid="tasks-comment-item-reply"
                  onClick={() => onReply(comment.id ?? '', authorName)}
                >
                  <Reply className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('activity.comment.replyAria')}</TooltipContent>
            </Tooltip>
          )}

          {isOwn && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 text-muted-foreground hover:text-destructive"
                  aria-label={t('activity.comment.deleteAria')}
                  data-testid="tasks-comment-item-delete"
                  onClick={() => onDelete(comment.id ?? '')}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('activity.comment.deleteAria')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
